import {
  appSync,
  startListening,
  selectAuthenticationStatus,
  initiateLogin,
  appAuthenticated,
  displayLastLaunchedGameToast,
  socketConnected,
  trackEvent,
} from '@bonanza-social/toolkit/state';
import {
  fetchGames,
  launchGame,
  selectGameIsUnderMaintenance,
  selectGames,
  selectGamesState,
  setLastLaunchedGame,
  setShowLastGameLaunchedToast,
  updateMaintenance,
} from './games.slice';
import { selectAccount } from '@bonanza-social/account';
import { getKey } from '@bonanza-social/i18n';
import { getLockedLabel } from '../../components/Games/Game/Game.utils';
import { BottomSheet, navigationRef } from '@bonanza-social/navigation';
import { selectIsTestAccount } from '@bonanza-social/account';
import { ProviderMaintenance } from './games.types';
import { AsyncStorage } from '@bonanza-social/toolkit/storage';
import { ValidationError } from '@bonanza-social/toolkit/api';
import { logError } from '@bonanza-social/toolkit/logging';

startListening({
  actionCreator: appSync,
  effect: (_, { dispatch }) => {
    dispatch(fetchGames('authenticated'));
  },
});

startListening({
  actionCreator: launchGame,
  effect: async (action, { delay, dispatch, getState, condition }) => {
    const { id, currencyCode, onError } = action.payload;
    const gamesDataState = selectGamesState(getState());
    const authStatus = selectAuthenticationStatus(getState());

    dispatch(
      trackEvent({
        name: 'game_launch',
        params: {
          gameId: id,
        },
      }),
    );

    if (authStatus === 'unauthenticated') {
      dispatch(initiateLogin());

      await condition(appAuthenticated.match);
      await condition(fetchGames.fulfilled.match);
    }

    if (gamesDataState === 'idle' || gamesDataState === 'pending') {
      await condition(fetchGames.fulfilled.match);
    }

    const state = getState();
    const { status, statusReason, accountId } = selectAccount(state) || {};
    const games = selectGames(state);
    const game = games[id];
    const releaseDate = game && game.displayFeatures.ComingSoon?.validUntil;
    const isUnderMaintenance = selectGameIsUnderMaintenance(getState(), id);
    const isTestAccount = selectIsTestAccount(getState());

    try {
      if (!game) {
        throw new ValidationError(getKey('gameNotFound', { ns: 'games' }));
      }

      if (status === 'Timeout') {
        throw new ValidationError(getKey('userOnBreak', { ns: 'games' }));
      }

      if (status === 'Suspended') {
        throw new ValidationError(getKey('userSuspendedGame', { ns: 'games' }));
      }

      if (
        status === 'Pending' &&
        (statusReason === 'KYC incomplete' ||
          statusReason === 'KYC in progress')
      ) {
        throw new ValidationError(getKey('userPendingGame', { ns: 'games' }));
      }

      if (releaseDate) {
        throw new ValidationError(
          getKey('gameNotReleased', {
            ns: 'games',
            date: getLockedLabel(releaseDate),
          }),
        );
      }

      if (isUnderMaintenance && !isTestAccount) {
        throw new ValidationError(
          getKey('maintenanceDescription', {
            ns: 'games',
          }),
          { cause: 'Maintenance' },
        );
      }

      const eligibleToPlay = await AsyncStorage.getItem('eligible_to_play');

      if (eligibleToPlay === 'false') {
        const confirmed = await new Promise<boolean>((resolve) => {
          BottomSheet.open({
            namespace: 'games',
            type: 'GameplayEligibility',
            titleNS: 'games',
            titleKey: 'gameplayEligibilityTitle',
            onInfo: () => {
              BottomSheet.open({
                namespace: 'common',
                type: 'Info',
                data: {
                  textNS: 'games',
                  textKey: 'gameplayEligibilityInfo',
                },
              });
            },
            data: {
              onConfirm: () => resolve(true),
              onClose: () => resolve(false),
            },
          });
        });

        if (!confirmed) {
          return;
        }
      }

      dispatch(setLastLaunchedGame({ [accountId!]: { id, currencyCode } }));
      dispatch(setShowLastGameLaunchedToast(true));
      navigationRef.navigate('GameplayScreen', { id, currencyCode });
    } catch (error) {
      if (onError) {
        onError?.();

        await delay(0); // Required when performing navigation inside the onError callback to prevent the bottom sheet from closing
      }

      if (error instanceof ValidationError) {
        const type =
          error.cause === 'Maintenance' ? 'Maintenance' : 'InfoImage';

        BottomSheet.open({
          namespace: 'common',
          type,
          data: {
            image: 'common.rejected',
            titleNS: 'common',
            titleKey: 'errorTitle',
            description: error.message,
          },
        });
      } else {
        BottomSheet.open({
          namespace: 'common',
          type: 'InfoImage',
          data: {
            image: 'common.rejected',
            titleNS: 'common',
            titleKey: 'errorTitle',
            descriptionNS: 'common',
            descriptionKey: 'errorDescription',
          },
        });

        logError('Launch game error', error);
      }
    }
  },
});

startListening({
  actionCreator: displayLastLaunchedGameToast,
  effect: (_, { dispatch }) => {
    dispatch(setShowLastGameLaunchedToast(true));
  },
});

startListening({
  actionCreator: socketConnected,
  effect: (action, { dispatch, getState }) => {
    const socketConnection = action.payload;
    const authStatus = selectAuthenticationStatus(getState());

    socketConnection.on(
      'MaintenanceScheduleUpdatedAsync',
      (notification: ProviderMaintenance) => {
        if (notification) {
          const { gameIds, isUnderMaintenance } = notification;
          dispatch(
            updateMaintenance({ authStatus, gameIds, isUnderMaintenance }),
          );
        }
      },
    );
  },
});
